import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EuiErrorBoundary } from '@elastic/eui';
import { extractGraphQLError, handleApolloError } from 'utils/errorHandlers';
import { CONSTANTS } from 'utils/constants';
import { useGetDevicesByOrganizationQuery, useGetGroupDevicesQuery } from 'gqlHooks';
import type { ApolloError } from '@apollo/client';
import type { GenericRow } from 'gqlHooks';
import { errorToastMessage } from 'app/utils/toast-messages';
import GroupsInMemoryTable from 'app/layout/groups/devices/in-memory-table/groups-in-memory-table';
import DeviceListExport from 'layout/device-list/export/device-list-export';
import { Toast } from '@elastic/eui/src/components/toast/global_toast_list';

type DeviceListTableProps = {
  organization: string;
  organizationName?: string;
  groups: GenericRow[];
  addToastMessage: (message: Toast) => void;
};

export interface Device extends GenericRow {
  identifier: string;
  label: string;
  organizationId: string;
}

const DEBOUNCE_MS = 300;

export const DeviceListTable = (props: DeviceListTableProps) => {
  const [selectedGroup, setSelectedGroup] = useState<GenericRow | undefined>(undefined);
  const [showAllDevices, setShowAllDevices] = useState(true);

  // Pagination state
  const [limit, setLimit] = useState<number>(CONSTANTS.QUERY_LIMITS[1]); // 25
  const [currentPageToken, setCurrentPageToken] = useState<string | null | undefined>(null);
  const [prevTokens, setPrevTokens] = useState<Array<string | null | undefined>>([]);

  // Search state (immediate input value + debounced value sent to API)
  const [searchInputValue, setSearchInputValue] = useState('');
  const [debouncedDeviceId, setDebouncedDeviceId] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetPagination = useCallback(() => {
    setCurrentPageToken(null);
    setPrevTokens([]);
  }, []);

  const handleError = useCallback((error: ApolloError) => {
    const gqlError = extractGraphQLError(error);
    handleApolloError(error, true, () => console.error(gqlError.errorMessage));
  }, []);

  // Reset pagination and search whenever the org changes
  useEffect(() => {
    setShowAllDevices(true);
    setSelectedGroup(undefined);
    resetPagination();
    setSearchInputValue('');
    setDebouncedDeviceId('');
  }, [props.organization, resetPagination]);

  // Debounce search: reset to page 1 each time the user types
  const onSearchChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        setDebouncedDeviceId(value);
        resetPagination();
      }, DEBOUNCE_MS);
    },
    [resetPagination]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // When group selection changes, reset pagination
  const handleGroupSelect = useCallback(
    (group: GenericRow | undefined) => {
      setSelectedGroup(group);
      setShowAllDevices(!group);
      resetPagination();
    },
    [resetPagination]
  );

  const onNextPage = useCallback(
    (nextToken: string | null | undefined) => {
      setPrevTokens(prev => [...prev, currentPageToken]);
      setCurrentPageToken(nextToken);
    },
    [currentPageToken]
  );

  const onPrevPage = useCallback(() => {
    setPrevTokens(prev => {
      const next = [...prev];
      const token = next.pop();
      setCurrentPageToken(token);
      return next;
    });
  }, []);

  const onLimitChange = useCallback(
    (newLimit: number) => {
      setLimit(newLimit);
      resetPagination();
    },
    [resetPagination]
  );

  // Single-page org devices query (replaces useAllDevicesByOrganization load-all)
  const {
    loading: deviceLoading,
    data: orgDevicesData,
    error: orgDevicesError,
  } = useGetDevicesByOrganizationQuery({
    variables: {
      organizationId: props.organization,
      limit,
      nextToken: currentPageToken,
      searchTerm: debouncedDeviceId || undefined,
    },
    skip: !props.organization || !showAllDevices,
    fetchPolicy: 'network-only',
    onError: handleError,
  });

  // Single-page group devices query
  const { loading: loadingDevices, data: groupDevicesData } = useGetGroupDevicesQuery({
    variables: {
      organizationId: props.organization,
      groupToken: selectedGroup?.identifier ?? '',
      limit,
      recursive: false,
      nextToken: currentPageToken,
      searchTerm: debouncedDeviceId || undefined,
    },
    skip: showAllDevices || !selectedGroup,
    onCompleted: data =>
      // @ts-expect-error window heap is defined
      window.heap?.track('GroupDeviceLoad', {
        total: data?.groupDevices?.items?.length,
        organization: props.organization,
      }),
    onError: () => props.addToastMessage(errorToastMessage('Failed to load devices in group')),
  });

  // Heap tracking for org device loads
  useEffect(() => {
    if (orgDevicesData && !orgDevicesError) {
      // @ts-expect-error window heap is defined
      window.heap?.track('DeviceLoad', {
        total: orgDevicesData?.devices?.items?.length,
        organization: props.organization,
      });
    }
  }, [orgDevicesData, orgDevicesError, props.organization]);

  const currentItems = showAllDevices
    ? (orgDevicesData?.devices?.items ?? []).map(org => ({
        ...org,
        identifier: org.identifier ?? '',
        label: org.label ?? '',
        organizationId: org.organizationId ?? '',
      }))
    : groupDevicesData?.groupDevices?.items ?? [];

  const nextPageToken = showAllDevices
    ? orgDevicesData?.devices?.nextToken
    : groupDevicesData?.groupDevices?.nextToken;

  const isOrgView = showAllDevices;
  const isTableLoading = isOrgView ? deviceLoading : loadingDevices;

  const isExportDisabled =
    !props.organization ||
    (!isOrgView && !selectedGroup) ||
    isTableLoading ||
    (currentItems.length === 0 && !nextPageToken);

  return (
    <EuiErrorBoundary>
      <GroupsInMemoryTable
        setShowAllDevices={setShowAllDevices}
        isLoading={loadingDevices || deviceLoading}
        groups={props.groups}
        devices={currentItems}
        selectedGroup={selectedGroup}
        setSelectedGroup={handleGroupSelect}
        // Server-side search + external pagination
        deviceIdSearch={searchInputValue}
        onDeviceIdSearchChange={onSearchChange}
        onNextPage={() => onNextPage(nextPageToken)}
        onPrevPage={onPrevPage}
        onLimitChange={onLimitChange}
        pageSize={limit}
        hasNextPage={Boolean(nextPageToken)}
        hasPrevPage={prevTokens.length > 0}
        exportSlot={
          <DeviceListExport
            organizationId={props.organization}
            organizationName={props.organizationName}
            searchTerm={debouncedDeviceId || undefined}
            groupToken={selectedGroup?.identifier ?? undefined}
            groupName={selectedGroup?.label ?? undefined}
            isDisabled={isExportDisabled}
            addToastMessage={props.addToastMessage}
          />
        }
      />
    </EuiErrorBoundary>
  );
};

export const MemoizedDeviceListTable = React.memo(DeviceListTable);
